import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { adminFetch } from '../../utils/adminApi';
import ImageUrlField from './ImageUrlField';
import './AdminSeoPanels.css';

const TRACKING_FIELDS = [
  { key: 'gtmId', label: 'Google Tag Manager', placeholder: 'GTM-XXXXXXX', hint: 'Container ID from tagmanager.google.com' },
  { key: 'ga4Id', label: 'Google Analytics 4', placeholder: 'G-XXXXXXXXXX', hint: 'Measurement ID from GA4 property' },
  { key: 'googleAdsId', label: 'Google Ads Conversion', placeholder: 'AW-XXXXXXXXX', hint: 'Conversion tracking ID' },
  { key: 'metaPixelId', label: 'Meta (Facebook) Pixel', placeholder: '123456789012345', hint: 'Pixel ID from Events Manager' },
  { key: 'linkedinPartnerId', label: 'LinkedIn Insight Tag', placeholder: '1234567', hint: 'Partner ID from Campaign Manager' },
  { key: 'clarityId', label: 'Microsoft Clarity', placeholder: 'abcdefghij', hint: 'Project ID from clarity.microsoft.com' },
];

const TechnicalSeoPanel = () => {
  const [settings, setSettings] = useState({});
  const [redirects, setRedirects] = useState([]);
  const [redirectForm, setRedirectForm] = useState({ fromPath: '', toPath: '', type: '301', note: '' });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const [sRes, rRes] = await Promise.all([
      fetch('/api/site-settings'),
      adminFetch('/api/redirects'),
    ]);
    if (sRes.ok) setSettings(await sRes.json());
    if (rRes.ok) setRedirects(await rRes.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setLoading(true);
    const res = await adminFetch('/api/site-settings', { method: 'PUT', body: JSON.stringify(settings) });
    setLoading(false);
    if (res.ok) Swal.fire('Saved', 'Settings updated.', 'success');
    else Swal.fire('Error', 'Could not save.', 'error');
  };

  const addRedirect = async () => {
    setLoading(true);
    const res = await adminFetch('/api/redirects', { method: 'POST', body: JSON.stringify(redirectForm) });
    setLoading(false);
    if (res.ok) {
      setRedirectForm({ fromPath: '', toPath: '', type: '301', note: '' });
      load();
    } else Swal.fire('Error', 'Could not add redirect.', 'error');
  };

  return (
    <div className="admin-seo-panel animate-in">
      <h2 className="admin-seo-panel-title">Technical SEO & Tracking</h2>
      <p className="admin-seo-panel-desc">Site-wide settings, tracking IDs, redirects, and robots.txt. Per-product/category SEO lives in those forms.</p>

      <section className="admin-seo-section">
        <h3>Rollout Flags</h3>
        <p className="hint">Enable one at a time after testing. All OFF = site behaves as before.</p>
        <div className="flag-grid">
          {['useSeoMiddleware', 'useSlugUrls', 'useCmsPages', 'useDynamicRobots'].map((flag) => (
            <label key={flag} className="flag-label">
              <input type="checkbox" checked={!!settings[flag]} onChange={(e) => setSettings({ ...settings, [flag]: e.target.checked })} />
              {flag.replace('use', '').replace(/([A-Z])/g, ' $1').trim()}
            </label>
          ))}
        </div>
        <div className="form-row one-col">
          <label>Site URL<input value={settings.siteUrl || ''} onChange={(e) => setSettings({ ...settings, siteUrl: e.target.value })} placeholder="https://smarttelecom.nl" /></label>
        </div>
        <ImageUrlField
          label="Default OG Image"
          value={settings.defaultOgImage || ''}
          onChange={(url) => setSettings({ ...settings, defaultOgImage: url })}
          folder="seo"
          placeholder="https://... or upload"
        />
      </section>

      <section className="admin-seo-section">
        <h3>Tracking & Analytics IDs</h3>
        <p className="hint">Paste each platform ID — scripts inject automatically when SEO middleware is enabled.</p>
        <div className="tracking-grid">
          {TRACKING_FIELDS.map(({ key, label, placeholder, hint }) => (
            <div key={key} className="tracking-field">
              <label>{label}</label>
              <input
                value={settings[key] || ''}
                onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                placeholder={placeholder}
              />
              <small>{hint}</small>
            </div>
          ))}
        </div>
        <label className="full-width">Additional Head Scripts<textarea rows={2} value={settings.additionalHeadScripts || ''} onChange={(e) => setSettings({ ...settings, additionalHeadScripts: e.target.value })} placeholder="Custom script tags for <head>" /></label>
        <label className="full-width">Additional Body Scripts<textarea rows={2} value={settings.additionalBodyScripts || ''} onChange={(e) => setSettings({ ...settings, additionalBodyScripts: e.target.value })} placeholder="Custom script tags before </body>" /></label>
        <div className="events-ref">
          <strong>GTM Events (auto-fired):</strong>
          <code>contact_form_submit</code> · <code>button_click</code> · <code>phone_click</code> · <code>whatsapp_click</code> · <code>email_click</code> · <code>newsletter_signup</code> · <code>purchase</code>
        </div>
      </section>

      <section className="admin-seo-section">
        <h3>robots.txt & Sitemap</h3>
        <label className="flag-label" style={{ marginBottom: 14, maxWidth: 420 }}>
          <input type="checkbox" checked={!!settings.useDynamicRobots} onChange={(e) => setSettings({ ...settings, useDynamicRobots: e.target.checked })} />
          Use admin robots.txt (otherwise static file is served)
        </label>
        <label className="full-width">robots.txt content
          <textarea rows={6} value={settings.robotsTxt || ''} onChange={(e) => setSettings({ ...settings, robotsTxt: e.target.value })} />
        </label>
        <p className="hint">Sitemap auto-generated at <a href="/sitemap.xml" target="_blank" rel="noreferrer" style={{ color: '#be1e2d' }}>/sitemap.xml</a></p>
      </section>

      <section className="admin-seo-section">
        <h3>404 Page</h3>
        <div className="form-row">
          <label>Title<input value={settings.custom404Title || ''} onChange={(e) => setSettings({ ...settings, custom404Title: e.target.value })} /></label>
          <label>Redirect URL (optional)<input value={settings.custom404RedirectUrl || ''} onChange={(e) => setSettings({ ...settings, custom404RedirectUrl: e.target.value })} /></label>
        </div>
        <label className="full-width">Message<textarea rows={2} value={settings.custom404Message || ''} onChange={(e) => setSettings({ ...settings, custom404Message: e.target.value })} /></label>
      </section>

      <section className="admin-seo-section">
        <h3>URL Redirects (301 / 302)</h3>
        <div className="form-row three-col">
          <label>From<input value={redirectForm.fromPath} onChange={(e) => setRedirectForm({ ...redirectForm, fromPath: e.target.value })} placeholder="/old-url" /></label>
          <label>To<input value={redirectForm.toPath} onChange={(e) => setRedirectForm({ ...redirectForm, toPath: e.target.value })} placeholder="/new-url" /></label>
          <label>Type
            <select value={redirectForm.type} onChange={(e) => setRedirectForm({ ...redirectForm, type: e.target.value })}>
              <option value="301">301 Permanent</option>
              <option value="302">302 Temporary</option>
            </select>
          </label>
        </div>
        <button type="button" className="action-submit-btn secondary" onClick={addRedirect} disabled={loading}>Add Redirect</button>
        {redirects.length > 0 && (
          <table className="admin-seo-table">
            <thead><tr><th>From</th><th>To</th><th>Type</th><th>Active</th></tr></thead>
            <tbody>
              {redirects.map((r) => (
                <tr key={r._id}>
                  <td>{r.fromPath}</td>
                  <td>{r.toPath}</td>
                  <td>{r.type}</td>
                  <td>{r.isActive ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div className="admin-seo-actions">
        <button type="button" className="action-submit-btn" onClick={save} disabled={loading}>Save All Settings</button>
      </div>
    </div>
  );
};

export default TechnicalSeoPanel;
